/**
 * Helpers for evaluating expression defaults when creating schema models in the designer.
 */

/**
 * @param {unknown} defaultValue
 * @returns {boolean}
 */
export function isExpressionSchemaDefault(defaultValue) {
    return typeof defaultValue === 'string'
        && (defaultValue.includes('{{') || defaultValue.includes('{*'));
}

/**
 * @param {unknown} defaultValue
 * @param {unknown} context
 * @param {{ compile?: Function, evaluate?: Function }} [deps]
 * @returns {unknown}
 */
export function evaluateExpressionSchemaDefault(defaultValue, context, deps) {
    if (!isExpressionSchemaDefault(defaultValue))
        return defaultValue;

    const compile = deps?.compile;
    const evaluate = deps?.evaluate;
    if (typeof compile !== 'function' || typeof evaluate !== 'function')
        return '';

    const compiled = compile(context, defaultValue);
    if (!compiled || !compiled.func)
        return '';

    const result = evaluate(compiled, context);
    return result == null ? '' : result;
}

/**
 * @param {unknown} model
 * @param {unknown} schema
 * @param {unknown} context
 * @param {{ resolve_Of?: Function, compile?: Function, evaluate?: Function }} [deps]
 */
export function applyExpressionSchemaDefaults(model, schema, context, deps) {
    if (!model || !schema || !context)
        return;

    const resolve_Of = deps?.resolve_Of;
    if (typeof resolve_Of !== 'function')
        return;

    const resolved = resolve_Of(schema);
    if (resolved.type !== 'object' || !resolved.properties)
        return;

    for (const key in resolved.properties) {
        const propertySchema = resolve_Of(resolved.properties[key]);

        if ('default' in propertySchema
            && isExpressionSchemaDefault(propertySchema.default)) {
            const shouldApply = !(key in model)
                || model[key] === undefined
                || model[key] === null
                || model[key] === propertySchema.default;

            if (shouldApply) {
                model[key] = evaluateExpressionSchemaDefault(
                    propertySchema.default,
                    context,
                    deps,
                );
            }
        }

        if (propertySchema.properties
            && model[key]
            && typeof model[key] === 'object'
            && !Array.isArray(model[key]))
            applyExpressionSchemaDefaults(model[key], propertySchema, context, deps);
    }
}

export const _isExpressionSchemaDefault = isExpressionSchemaDefault;
export const _evaluateExpressionSchemaDefault = evaluateExpressionSchemaDefault;
export const _applyExpressionSchemaDefaults = applyExpressionSchemaDefaults;
